import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material'
import {
  Restaurant,
  Logout,
  Assignment,
  ExpandMore,
  ExpandLess,
  Payment,
  Cancel
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'
import { parseISO, addYears } from 'date-fns'

const EducacaoDashboard = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [expandedEditais, setExpandedEditais] = useState({})
  const [paymentStatus, setPaymentStatus] = useState({})

  useEffect(() => {
    fetchEditais()
  }, [])

  const fetchEditais = async () => {
    try {
      setLoading(true)
      const response = await api.get('/editais/list.php')
      if (response.data.success) {
        const editaisWithConcorrentes = await Promise.all(
          (response.data.editais || []).map(async (edital) => {
            try {
              const concorrentesResponse = await api.get(
                `/editais/concorrentes.php?edital_id=${edital.id}`
              )
              const concorrentes = concorrentesResponse.data.concorrentes || []
              
              // Carregar status de pagamento
              const statusMap = {}
              concorrentes.forEach((c) => {
                if (c.selecionado === 1) {
                  statusMap[c.id] = {
                    pago: c.pago === 1,
                    motivo_nao_pago: c.motivo_nao_pago || ''
                  }
                }
              })
              
              return {
                ...edital,
                concorrentes,
                paymentStatus: statusMap
              }
            } catch (err) {
              return { ...edital, concorrentes: [], paymentStatus: {} }
            }
          })
        )
        setEditais(editaisWithConcorrentes)
        setPaymentStatus(
          editaisWithConcorrentes.reduce((acc, e) => {
            acc[e.id] = e.paymentStatus || {}
            return acc
          }, {})
        )
      } else {
        setError(response.data.message || 'Erro ao carregar editais')
      }
    } catch (err) {
      setError('Erro ao carregar editais. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleEdital = (editalId) => {
    setExpandedEditais((prev) => ({
      ...prev,
      [editalId]: !prev[editalId]
    }))
  }

  const handlePaymentChange = async (editalId, concorrenteId, value) => {
    try {
      const response = await api.post('/editais/atualizar-pagamento.php', {
        edital_id: editalId,
        concorrente_id: concorrenteId,
        pago: value === 'pago' ? 1 : 0,
        motivo_nao_pago: value !== 'pago' ? value : ''
      })

      if (response.data.success) {
        setPaymentStatus((prev) => ({
          ...prev,
          [editalId]: {
            ...(prev[editalId] || {}),
            [concorrenteId]: {
              pago: value === 'pago',
              motivo_nao_pago: value !== 'pago' ? value : ''
            }
          }
        }))
        setSuccess('Status de pagamento atualizado!')
        setTimeout(() => setSuccess(''), 3000)
        fetchEditais()
      } else {
        setError(response.data.message || 'Erro ao atualizar pagamento')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao atualizar pagamento')
    }
  }

  const isCAFExpired = (dataInscricao) => {
    if (!dataInscricao) return true
    const inscricaoDate = parseISO(dataInscricao)
    const expirationDate = addYears(inscricaoDate, 3)
    const today = new Date()
    return today > expirationDate
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Educação - Controle de Pagamentos
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.nome}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Gestão de Pagamentos
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum edital cadastrado
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editais.map((edital) => {
              const expanded = expandedEditais[edital.id]
              const selecionados = edital.concorrentes?.filter((c) => c.selecionado === 1) || []
              const statusMap = paymentStatus[edital.id] || {}

              return (
                <Card key={edital.id} elevation={2}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      <Box>
                        <Typography variant="h6">{edital.titulo}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {edital.descricao}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => handleToggleEdital(edital.id)}>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Início:</strong>{' '}
                        {new Date(edital.data_inicio).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fim:</strong>{' '}
                        {new Date(edital.data_fim).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fornecedores:</strong> {selecionados.length}
                      </Typography>
                    </Box>

                    {expanded && (
                      <Box sx={{ mt: 3 }}>
                        {selecionados.length > 0 ? (
                          <>
                            <Typography variant="subtitle1" gutterBottom>
                              Fornecedores Selecionados - Controle de Pagamento:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {selecionados.map((concorrente) => {
                                const status = statusMap[concorrente.id] || {
                                  pago: false,
                                  motivo_nao_pago: ''
                                }
                                const cafExpired = isCAFExpired(concorrente.data_inscricao_caf)

                                return (
                                  <Paper
                                    key={concorrente.id}
                                    sx={{ p: 2, bgcolor: 'background.default' }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1
                                      }}
                                    >
                                      <Box>
                                        <Typography variant="body1" fontWeight="bold">
                                          {concorrente.nome_produtor}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          CAF: {concorrente.caf} | CPF: {concorrente.cpf}
                                        </Typography>
                                        {cafExpired && (
                                          <Chip
                                            label="CAF Expirado"
                                            color="warning"
                                            size="small"
                                            sx={{ mt: 1 }}
                                          />
                                        )}
                                      </Box>
                                      <Chip
                                        label={status.pago ? 'Pago' : 'Não Pago'}
                                        color={status.pago ? 'success' : 'error'}
                                        icon={status.pago ? <Payment /> : <Cancel />}
                                      />
                                    </Box>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                      <InputLabel>Status de Pagamento</InputLabel>
                                      <Select
                                        value={
                                          status.pago
                                            ? 'pago'
                                            : status.motivo_nao_pago || 'caf_expirado'
                                        }
                                        label="Status de Pagamento"
                                        onChange={(e) =>
                                          handlePaymentChange(
                                            edital.id,
                                            concorrente.id,
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="pago">Pago</MenuItem>
                                        <MenuItem value="caf_expirado">
                                          CAF Expirado
                                        </MenuItem>
                                        <MenuItem value="documentacao_incompleta">
                                          Documentação Incompleta
                                        </MenuItem>
                                        <MenuItem value="fornecedor_inativo">
                                          Fornecedor Inativo
                                        </MenuItem>
                                        <MenuItem value="outro">Outro Motivo</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Paper>
                                )
                              })}
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum fornecedor selecionado para este edital.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default EducacaoDashboard

